import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { inviteFieldUser } from "@/lib/api";

const ROLES = [{ value: "technician", label: "Technician" }];

// Admin / Service Manager invite: name, email, optional phone. Role is
// always Technician — Lead Techs sign in with JumpCloud. Saving links or
// creates the technician roster row and emails the magic link.
// `prefill` lets the Technicians tab open this same dialog with the
// roster row's details already filled in (iter300) — identical submit,
// identical roster linking.
export default function InviteFieldUserDialog({ onDone, onCancel, prefill }) {
  const [form, setForm] = useState({
    name: prefill?.name || "", email: prefill?.email || "",
    role: "technician", phone: prefill?.phone || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await inviteFieldUser({ ...form, name: form.name.trim(), email: form.email.trim().toLowerCase(), phone: form.phone.trim() || null });
      toast.success(`Invite ${data.email_status === "sent" ? "emailed" : `logged (${data.email_status})`} to ${data.email} · roster ${data.roster_link?.roster || "linked"}`);
      onDone();
    } catch (err) {
      setError(err?.response?.data?.detail || "Invite failed");
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="sm:max-w-sm" data-testid="dialog-invite-field-user">
      <DialogHeader>
        <DialogTitle>Invite Field User</DialogTitle>
        <DialogDescription>{prefill ? `Invite ${prefill.name} to sign in on their phone. ` : ""}They receive a one-time sign-in link (15 min). Magic-link access is for Technicians only — Lead Technicians and anyone already in JumpCloud sign in with JumpCloud.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        {error && <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1.5" data-testid="invite-error">{error}</div>}
        <div><Label htmlFor="fi-name">Name</Label><Input id="fi-name" required value={form.name} onChange={set("name")} data-testid="invite-name" /></div>
        <div><Label htmlFor="fi-email">Email</Label><Input id="fi-email" type="email" required value={form.email} onChange={set("email")} data-testid="invite-email" /></div>
        <div>
          <Label>Role</Label>
          <Select value={form.role} onValueChange={set("role")}>
            <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value} data-testid={`invite-role-${r.value}`}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label htmlFor="fi-phone">Phone (optional)</Label><Input id="fi-phone" value={form.phone} onChange={set("phone")} data-testid="invite-phone" /></div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel} data-testid="invite-cancel">Cancel</Button>
          <Button type="submit" disabled={busy} data-testid="invite-submit">{busy ? "Sending…" : "Send invite"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
